import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usuarioService from '../services/UsuarioService';
import LoadingSpinner from './common/LoadingSpinner';
import logoAnimacion from '../assets/logos/logoAnimacion.png';
import Alert from './common/Alert';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import colegioApiService from '../services/ColegioApiService';
import { canCreateAssistant } from '../utils/userPermissions';
import './Login.css';
import './LoginPro.css';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetCaptchaToken, setResetCaptchaToken] = useState('');
  const [resetCaptchaError, setResetCaptchaError] = useState('');
  const resetCaptchaRef = useRef(null);
  const resetCaptchaWidgetId = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    type: 'error',
    title: ''
  });

  useEffect(() => {
    if (!showResetModal) return;
    if (window.turnstile || document.getElementById('turnstile-script')) return;

    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, [showResetModal]);

  useEffect(() => {
    if (!showResetModal) return undefined;
    if (resetCaptchaWidgetId.current !== null) return undefined;

    const timer = setInterval(() => {
      if (!resetCaptchaRef.current || resetCaptchaWidgetId.current !== null) return;

      if (!window.turnstile) return;

      if (!turnstileSiteKey) {
        setResetCaptchaError('Captcha no configurado. Contacta al administrador.');
        clearInterval(timer);
        return;
      }

      resetCaptchaWidgetId.current = window.turnstile.render(resetCaptchaRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => {
          setResetCaptchaToken(token);
          setResetCaptchaError('');
        },
        'expired-callback': () => {
          setResetCaptchaToken('');
          setResetCaptchaError('El captcha expiró. Completalo nuevamente.');
        },
        'error-callback': () => {
          setResetCaptchaToken('');
          setResetCaptchaError('Captcha inválido. Intenta nuevamente.');
        }
      });
      clearInterval(timer);
    }, 200);

    return () => clearInterval(timer);
  }, [showResetModal, turnstileSiteKey]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos
    if (!formData.email || !formData.password) {
      setAlert({
        show: true,
        title: 'Campos incompletos',
        message: 'Por favor completa todos los campos antes de continuar.',
        type: 'warning'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Llamar al servicio de usuario para hacer login
      const response = await usuarioService.login(formData.email, formData.password);

      const roleNameValue = response.nombreRol || response.roleName || '';
      const roleNameLower = roleNameValue.toLowerCase();
      const isAdmin = roleNameLower.includes('admin');
      const isAdminArea = roleNameLower.includes('adminarea');
      const isAdminGeneral = isAdmin && !isAdminArea;
      const isProfesor = roleNameLower.includes('profesor');
      const isAlumno = roleNameLower.includes('alumno');
      const isParticularRole = roleNameLower.includes('particular');
      const isAlumnoParticular = roleNameLower.includes('alumnoparticular') || (isAlumno && isParticularRole);

      // Normalizar datos adicionales del login
      const rawColegios = response.colegios || response.Colegios || [];
      const rawParticulares = response.particulares || response.Particulares || [];
      const rawAreas = response.areas || response.Areas || [];
      const rawMaterias = response.materias || response.Materias || [];
      const rawFeatureFlags = response.ffColegio || response.FFColegio || [];
      const onlyActivos = (list) =>
        Array.isArray(list)
          ? list.filter((item) => {
              const activo = item?.activo ?? item?.Activo;
              return activo === undefined ? true : Boolean(activo);
            })
          : [];
      const colegios = onlyActivos(rawColegios);
      const particulares = onlyActivos(rawParticulares);
      const areas = onlyActivos(rawAreas);
      const materias = onlyActivos(rawMaterias);
      const featureFlags = Array.isArray(rawFeatureFlags) ? rawFeatureFlags : [];
      
      // Si llegamos aquí, el login fue exitoso (response.ok === true)
      const assistantPermission = canCreateAssistant(response);
      const userData = {
        email: response.email,
        name: response.displayName,
        token: response.token,
        pictureUrl: response.pictureUrl || '',
        permiteCrearAsistente: assistantPermission,
        pertmiteCrearAsistente: assistantPermission,
        permiteCrearAula: response.permiteCrearAula ?? response.PermiteCrearAula ?? false,
        statusCode: response.statusCode,
        idRol: response.idRol,
        nombreRol: response.nombreRol,
        particulares
      };

      // Guardar colegios, áreas y materias en localStorage
      ColegioService.setColegioData({ colegios, areas, materias });
      ParticularService.setParticulares(particulares);
      if (isProfesor || isAlumno || isParticularRole) {
        ColegioService.setFeatureFlags(featureFlags);
      }

      // Determinar colegio activo y ruta de redirección
      let redirectPath = isAlumno ? '/alumno/panel' : '/dashboard';
      if (isParticularRole) {
        ColegioService.setActiveColegio(null);
        if (particulares.length === 1) {
          ParticularService.setActiveParticular(particulares[0]);
          redirectPath = isAlumnoParticular ? '/particular/alumno-panel' : '/particular';
        } else if (particulares.length > 1) {
          ParticularService.setActiveParticular(null);
          redirectPath = '/select-colegio';
        } else {
          ParticularService.setActiveParticular(null);
          redirectPath = '/particular';
        }
      } else if (isAdmin) {
        ParticularService.setActiveParticular(null);
        ColegioService.setActiveColegio(null);
        redirectPath = '/admin/select-colegio';
        try {
          // Prefetch de colegios para admin (puede fallar sin bloquear flujo)
          const remote = isAdminGeneral
            ? await colegioApiService.getColegios()
            : await colegioApiService.getColegiosActivos();
          const remoteList = remote.colegios || remote.Colegios || remote.data || remote || [];
          if (Array.isArray(remoteList) && remoteList.length > 0) {
            ColegioService.setColegioData({ colegios: remoteList, areas, materias });
          }
        } catch (prefetchError) {
          console.warn('No se pudo prefetchear colegios admin:', prefetchError);
        }
      } else if (colegios.length === 1) {
        ParticularService.setActiveParticular(null);
        ColegioService.setActiveColegio(colegios[0]);
      } else if (colegios.length > 1) {
        ParticularService.setActiveParticular(null);
        ColegioService.setActiveColegio(null);
        if (!isAlumno) {
          redirectPath = '/select-colegio';
        }
      } else {
        ParticularService.setActiveParticular(null);
        ColegioService.setActiveColegio(null);
      }
      
      // Actualizar el contexto de autenticación (esto guardará automáticamente en cookies)
      await login(userData);

      // Redirigir al dashboard después del login exitoso
      navigate(redirectPath, { replace: true });
      
    } catch (error) {
      console.error('❌ Error en login:', error);
      
      // Mostrar mensaje priorizando el que devuelve el backend
      let errorMessage = error?.message || 'Error al iniciar sesión. Por favor, intenta nuevamente.';

      const lowerMsg = (errorMessage || '').toLowerCase();

      if (lowerMsg.includes('401') || lowerMsg.includes('unauthorized')) {
        errorMessage = 'Email o contraseña incorrectos. Verifica tus credenciales e intenta nuevamente.';
      } else if (lowerMsg.includes('404')) {
        errorMessage = 'Usuario no encontrado. Verifica tu email o solicita acceso al sistema.';
      } else if (lowerMsg.includes('500')) {
        errorMessage = 'Error del servidor. Por favor, intenta más tarde o contacta al administrador.';
      } else if (lowerMsg.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.';
      }
      
      setAlert({
        show: true,
        title: 'Error al iniciar sesión',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenResetModal = () => {
    setResetEmail(formData.email || '');
    setResetError('');
    setResetSuccess('');
    setShowResetModal(true);
  };

  const handleCloseResetModal = () => {
    if (resetLoading) return;
    setShowResetModal(false);
    setResetCaptchaToken('');
    setResetCaptchaError('');
    resetCaptchaWidgetId.current = null;
  };

  const handleSendResetEmail = async () => {
    const email = resetEmail.trim();
    if (!email) {
      setResetError('Ingresa tu email para continuar.');
      return;
    }

    if (!resetCaptchaToken) {
      setResetError('Completa el captcha para continuar.');
      return;
    }

    try {
      setResetLoading(true);
      setResetError('');
      setResetSuccess('');
      await usuarioService.requestPasswordReset(email, resetCaptchaToken);
      setResetSuccess('Te enviamos un email con el enlace para restablecer tu contraseña.');
      setResetCaptchaToken('');
      if (window.turnstile && resetCaptchaWidgetId.current !== null) {
        window.turnstile.reset(resetCaptchaWidgetId.current);
      }
    } catch (error) {
      setResetError(error.message || 'No pudimos enviar el email. Intenta nuevamente.');
      setResetCaptchaToken('');
      if (window.turnstile && resetCaptchaWidgetId.current !== null) {
        window.turnstile.reset(resetCaptchaWidgetId.current);
      }
    } finally {
      setResetLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  return (
    <div className="lg-page">
      <aside className="lg-aside">
        <Link className="lg-brand" to="/" aria-label="aluxIA, ir al inicio">
          <img src={logoAnimacion} alt="" />
          <span>aluxIA</span>
        </Link>

        <div className="lg-aside-copy">
          <p className="lg-eyebrow">Plataforma de asistentes educativos</p>
          <h1>
            Tu espacio de aprendizaje, <span>en un solo lugar</span>
          </h1>
          <p className="lg-aside-lead">
            Aulas, materiales, clases en vivo y asistentes IA con contexto para colegios, profesores particulares y
            familias.
          </p>
          <ul className="lg-features">
            <li>
              <i className="fas fa-robot" aria-hidden="true"></i>
              <span>Asistentes IA especializados</span>
            </li>
            <li>
              <i className="fas fa-graduation-cap" aria-hidden="true"></i>
              <span>Contenido académico actualizado</span>
            </li>
            <li>
              <i className="fas fa-shield-alt" aria-hidden="true"></i>
              <span>Plataforma segura y confiable</span>
            </li>
          </ul>
        </div>

        <p className="lg-aside-foot">IA educativa con contexto, control y seguimiento.</p>
      </aside>

      <main className="lg-main">
        <div className="lg-card">
          <Link className="lg-brand lg-card-brand" to="/" aria-label="aluxIA, ir al inicio">
            <img src={logoAnimacion} alt="" />
            <span>aluxIA</span>
          </Link>

          <h2>Bienvenido</h2>
          <p className="lg-card-sub">Accede a tu plataforma de asistentes educativos</p>

          <form onSubmit={handleSubmit} className="lg-form">
            <div className="lg-field">
              <label htmlFor="email">Correo electrónico</label>
              <div className="lg-input">
                <i className="fas fa-envelope" aria-hidden="true"></i>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  required
                />
              </div>
            </div>

            <div className="lg-field">
              <label htmlFor="password">Contraseña</label>
              <div className="lg-input has-toggle">
                <i className="fas fa-lock" aria-hidden="true"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="lg-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
                </button>
              </div>
            </div>

            <div className="lg-options">
              <label className="lg-check">
                <input type="checkbox" />
                <span>Recordar sesión</span>
              </label>
              <button type="button" className="lg-link" onClick={handleOpenResetModal}>
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <button type="submit" className="lg-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <img src={logoAnimacion} alt="" />
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <span>Ingresar</span>
                  <i className="fas fa-arrow-right" aria-hidden="true"></i>
                </>
              )}
            </button>
          </form>

          <Link className="lg-back" to="/">
            <i className="fas fa-arrow-left" aria-hidden="true"></i>
            Volver al inicio
          </Link>
        </div>
      </main>

      {/* Loading Spinner */}
      {isLoading && (
        <LoadingSpinner 
          message="Iniciando sesión..." 
          size="medium" 
          color="primary" 
        />
      )}

      {showResetModal && (
        <div className="reset-modal-overlay" onClick={handleCloseResetModal}>
          <div className="reset-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Restablecer contraseña</h3>
            <p>Ingresá tu email y te enviaremos un enlace para restablecerla.</p>

            <label className="reset-modal-label" htmlFor="resetEmail">Email</label>
            <input
              id="resetEmail"
              type="email"
              className="reset-modal-input"
              placeholder="tu@email.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              disabled={resetLoading}
            />

            <div className="reset-modal-captcha" ref={resetCaptchaRef} />
            {resetCaptchaError && <div className="reset-modal-error">{resetCaptchaError}</div>}

            <p className="reset-modal-hint">
              El envío del email puede demorar unos minutos. Recordá que también podés contactarte con tu colegio
              o profesor particular para que te restablezcan la contraseña: suele ser más rápido.
            </p>

            {resetError && <div className="reset-modal-error">{resetError}</div>}
            {resetSuccess && <div className="reset-modal-success">{resetSuccess}</div>}

            <div className="reset-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseResetModal}
                disabled={resetLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendResetEmail}
                disabled={resetLoading || !resetCaptchaToken}
              >
                {resetLoading ? 'Enviando...' : 'Enviar email'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Alert Component */}
      <Alert
        title={alert.title}
        message={alert.message}
        type={alert.type}
        isVisible={alert.show}
        onClose={closeAlert}
      />
    </div>
  );
};

export default Login;
